import { closedRangeOf, sameClosedRange } from './renewals/closed-lines';
import { nyToday } from './renewals/renewal-lines';
import {
  Outlet,
  redirect,
  type LoaderFunctionArgs,
  type RouteObject,
  type ShouldRevalidateFunction,
} from 'react-router';
import type { Api, Me } from './auth/api';
import { HOME, signInPath } from './auth/next';
import { USER_ROUTE } from './auth/user';
import { ActivityPage, type ActivityLoaderData } from './pages/ActivityPage';
import { CodePage } from './pages/CodePage';
import { DeletedRecordsPage } from './pages/DeletedRecordsPage';
import {
  DefaultsSettingsPage,
  StatusesSettingsPage,
  type DefaultsLoaderData,
  UrgencySettingsPage,
} from './pages/DepartmentSettingsPages';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { AmsSettingsPage, type AmsSettingsLoaderData } from './pages/AmsSettingsPage';
import { IntegrationsPage, type IntegrationsLoaderData } from './pages/IntegrationsPage';
import { RecordPage, type RecordLoaderData } from './pages/RecordPage';
import { RenewalsPage, type RenewalsLoaderData } from './pages/RenewalsPage';
import { RenewalSheetsPage } from './pages/RenewalSheetsPage';
import { SetPasswordPage } from './pages/SetPasswordPage';
import { SettingsPage, type SettingsLoaderData } from './pages/SettingsPage';
import { TemplatesPage, type TemplatesLoaderData } from './pages/TemplatesPage';
import { SignInPage, type SignInLoaderData } from './pages/SignInPage';
import { rangeFromSearch } from './settings/activity-lines';
import { managesDepartment, seesAllRenewals, type Role } from './roles';
import { AppShell } from './shell/AppShell';
import { FetchNotices } from './shell/FetchNotices';
import { LoadError } from './shell/LoadError';
import { NotFound, RouteError } from './shell/SystemStates';
import { noCounts, type NavCounts } from './shell/nav-counts';

export type AppDeps = {
  api: Api;
  counts?: NavCounts;
  /** Loads a page from the server; false where there is no browser (tests). */
  reload?: (url: string) => boolean;
  /**
   * The address of the page the router shows now (its last finished navigation), or null when not
   * known. Not the browser's address bar: on Back and Forward the browser changes it before the
   * loaders run (Codex on 232). The app passes `routerPage(router)`.
   */
  here?: () => string | null;
};

const goHome = () => redirect(HOME);

/** A full page load of `url` from the server; false where there is no browser (tests). */
export function reloadPage(url: string): boolean {
  if (typeof window === 'undefined') return false;
  window.location.assign(url);
  return true;
}

/** The page a router shows now, as an address, for `AppDeps.here`. */
export function routerPage(router: { state: { location: { pathname: string; search: string } } }) {
  const { pathname, search } = router.state.location;
  return pathname + search;
}

/**
 * The same page: the same path, whatever its query or #part. A list's search, filters and tab live
 * in the query and change as someone types, so they never count as a move (Codex on 232).
 */
const samePage = (url: string, shown: string) =>
  new URL(url).pathname === new URL(shown, 'http://localhost').pathname;

/**
 * The lists keep their tab, filter and grouping in the address; changing those reloads nothing.
 * The Closed tab's "Closed between" range does: it decides which closed renewals the server sends.
 */
const listRevalidate: ShouldRevalidateFunction = ({
  currentUrl,
  nextUrl,
  defaultShouldRevalidate,
}) =>
  currentUrl.pathname === nextUrl.pathname &&
  currentUrl.search !== nextUrl.search &&
  sameClosedRange(currentUrl.searchParams, nextUrl.searchParams)
    ? false
    : defaultShouldRevalidate;

/** The Closed between days in the list's address, as the server takes them. */
const closedDaysOf = (request: Request) => {
  const { from, to } = closedRangeOf(new URL(request.url).searchParams, nyToday(new Date()));
  return { from, to };
};

const fromPath = (request: Request) => {
  const url = new URL(request.url);
  return url.pathname + url.search + url.hash;
};

export function appRoutes({
  api,
  counts = noCounts,
  reload = reloadPage,
  here = () => null,
}: AppDeps): RouteObject[] {
  let reloading = false;
  /** The signed-in user, or a redirect to Sign in that comes back to this page afterwards. */
  async function requireMe({ request }: LoaderFunctionArgs): Promise<Me> {
    // A deploy since this tab opened: on a move to another page, load the whole app again for that
    // page, so the new web build renders the new server's answers; the navigation waits for it.
    // Never when the page shown reads its data again (after a fetch finishes, a save): that would
    // drop what someone is typing. Several loaders run for one page: the first
    // starts the reload, the others wait with it.
    const shown = here();
    if (api.newVersion() && (reloading || !shown || !samePage(request.url, shown))) {
      reloading ||= reload(request.url);
      if (reloading) return new Promise<Me>(() => {});
    }
    const result = await api.me();
    if (result.kind === 'signed_in') return result.me;
    throw redirect(signInPath(fromPath(request), result.kind === 'expired'));
  }

  // Pages a role does not have in its sidebar send it home instead.
  const onlyIf = (allowed: (role: Role) => boolean) => async (args: LoaderFunctionArgs) => {
    const me = await requireMe(args);
    return allowed(me.user.role) ? null : goHome();
  };

  return [
    {
      path: '/',
      // Nothing to show while a redirect resolves.
      hydrateFallbackElement: null,
      errorElement: <LoadError />,
      children: [
        { index: true, loader: goHome },
        {
          path: 'sign-in',
          // Signed in already: straight to My renewals. Expired: the email of that session.
          loader: async (): Promise<SignInLoaderData | Response> => {
            const result = await api.me();
            if (result.kind === 'signed_in') return goHome();
            return { expiredEmail: result.kind === 'expired' ? result.email : null };
          },
          element: <SignInPage api={api} />,
        },
        { path: 'sign-in/code', element: <CodePage api={api} /> },
        { path: 'set-password', element: <SetPasswordPage api={api} /> },
        { path: 'forgot-password', element: <ForgotPasswordPage api={api} /> },
        {
          id: USER_ROUTE,
          loader: requireMe,
          // Every signed-in page, the Record too: the message when one of my fetches finishes.
          element: (
            <>
              <Outlet />
              <FetchNotices api={api} />
            </>
          ),
          // Every click reloads the user, so a role or status change applies at once.
          shouldRevalidate: () => true,
          children: [
            {
              element: <AppShell counts={counts} api={api} />,
              children: [
                {
                  // A page that fails to load shows its system card inside the shell (spec C7).
                  errorElement: <RouteError />,
                  children: [
                    {
                      path: 'renewals/my',
                      loader: async (args): Promise<RenewalsLoaderData> => {
                        await requireMe(args);
                        return api.renewals('my', closedDaysOf(args.request));
                      },
                      // A tab or filter change only changes the address: no reload of the list.
                      shouldRevalidate: listRevalidate,
                      element: <RenewalsPage key="my" page="my" api={api} />,
                    },
                    {
                      path: 'renewals/all',
                      loader: async (args): Promise<RenewalsLoaderData | Response> => {
                        const me = await requireMe(args);
                        if (!seesAllRenewals(me.user.role)) return goHome();
                        return api.renewals('all', closedDaysOf(args.request));
                      },
                      shouldRevalidate: listRevalidate,
                      element: <RenewalsPage key="all" page="all" api={api} />,
                    },
                    {
                      path: 'deleted',
                      loader: onlyIf(managesDepartment),
                      element: <DeletedRecordsPage />,
                    },
                    {
                      path: 'settings',
                      loader: async (args): Promise<SettingsLoaderData | Response> => {
                        const me = await requireMe(args);
                        if (!managesDepartment(me.user.role)) return goHome();
                        return { users: await api.listUsers() };
                      },
                      element: <SettingsPage api={api} />,
                    },
                    // Read only for now (spec C4, C5, C14): the rules the portal runs today.
                    {
                      path: 'settings/statuses',
                      loader: onlyIf(managesDepartment),
                      element: <StatusesSettingsPage />,
                    },
                    {
                      path: 'settings/urgency',
                      loader: onlyIf(managesDepartment),
                      element: <UrgencySettingsPage />,
                    },
                    {
                      path: 'settings/defaults',
                      loader: async (args): Promise<DefaultsLoaderData | Response> => {
                        const me = await requireMe(args);
                        if (!managesDepartment(me.user.role)) return goHome();
                        const [increase, followUp] = await Promise.all([
                          api.increaseRule(),
                          api.followUpDays(),
                        ]);
                        return { increase, followUp };
                      },
                      element: <DefaultsSettingsPage api={api} />,
                    },
                    {
                      // Manager and Admin; only the Admin edits in Phase 1 (spec C9, C14).
                      path: 'settings/templates',
                      loader: async (args): Promise<TemplatesLoaderData | Response> => {
                        const me = await requireMe(args);
                        if (!managesDepartment(me.user.role)) return goHome();
                        return api.templates();
                      },
                      element: <TemplatesPage api={api} />,
                    },
                    {
                      // The Manager and the Admin (the Manager keeps the AMS360 version current).
                      path: 'settings/ams360',
                      loader: async (args): Promise<AmsSettingsLoaderData | Response> => {
                        const me = await requireMe(args);
                        if (!managesDepartment(me.user.role)) return goHome();
                        return api.amsAddress();
                      },
                      element: <AmsSettingsPage api={api} />,
                    },
                    {
                      // The Manager and the Admin: the temporary upload of the renewal workbooks.
                      path: 'settings/sheets',
                      loader: async (args): Promise<null | Response> => {
                        const me = await requireMe(args);
                        if (!managesDepartment(me.user.role)) return goHome();
                        return null;
                      },
                      element: <RenewalSheetsPage api={api} />,
                    },
                    {
                      // Admin only: everyone else is sent to the Settings they have.
                      path: 'settings/integrations',
                      loader: async (args): Promise<IntegrationsLoaderData | Response> => {
                        const me = await requireMe(args);
                        if (me.user.role !== 'Admin') {
                          return managesDepartment(me.user.role) ? redirect('/settings') : goHome();
                        }
                        return api.codeRules();
                      },
                      element: <IntegrationsPage api={api} />,
                    },
                    {
                      path: 'settings/activity',
                      loader: async (args): Promise<ActivityLoaderData | Response> => {
                        const me = await requireMe(args);
                        if (me.user.role !== 'Admin') {
                          return managesDepartment(me.user.role) ? redirect('/settings') : goHome();
                        }
                        const range = rangeFromSearch(new URL(args.request.url).searchParams);
                        return api.activity(range ?? undefined);
                      },
                      element: <ActivityPage />,
                    },
                    // Signed in, but no page here: Page not found, inside the shell.
                    { path: '*', element: <NotFound /> },
                  ],
                },
              ],
            },
            {
              path: 'records/:id',
              loader: async (args): Promise<RecordLoaderData> => {
                // Before anything is read: a card's own copy from after this point stays (issue 291).
                const loadedAt = Date.now();
                await requireMe(args);
                const id = args.params.id ?? '';
                const view = await api.renewal(id);
                // The Dec page step's fetch; the Record still opens if only this part fails.
                const [fetch, steps, log] = view
                  ? await Promise.all([
                      api.fetchView(id).catch(() => null),
                      // Steps 2 to 6; the card asks again if only this part fails.
                      api.steps(id).catch(() => null),
                      // The Portal log; the card says so if only this part fails.
                      api.renewalLog(id).catch(() => null),
                    ])
                  : [null, null, null];
                return { view, fetch, steps, log, loadedAt };
              },
              element: <RecordPage api={api} />,
              errorElement: <RouteError />,
            },
          ],
        },
      ],
    },
  ];
}
